import { useId, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { Button, IconButton } from './buttons';
import { ModalParentContext, useModal } from './overlay-utils';

export type SheetDetent = 'medium' | 'large' | number;
export interface SheetProps { open: boolean; onOpenChange: (open: boolean) => void; title: string; children: ReactNode;
  description?: string; footer?: ReactNode; headerActions?: ReactNode; closeLabel?: string; dismissible?: boolean;
  detents?: readonly SheetDetent[]; initialDetent?: SheetDetent; className?: string }
const fraction = (detent: SheetDetent) => detent === 'medium' ? .5 : detent === 'large' ? .92 : Math.max(.2, Math.min(.98, detent));

export interface SheetHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: string; description?: string; leading?: ReactNode; trailing?: ReactNode;
  titleId?: string; descriptionId?: string; largeTitle?: boolean;
}

/** Also usable inside a shell-owned presentation: one leading cancel and trailing save toolbar. */
export function SheetHeader({ title, description, leading, trailing, titleId, descriptionId, largeTitle = true, className, ...props }: SheetHeaderProps) {
  return <header {...props} className={clsx('hylo-sheet-header', largeTitle && 'hylo-sheet-header--large', className)}>
    <div className="hylo-sheet-header__leading">{leading}</div>
    <h2 id={titleId}>{title}</h2>
    <div className="hylo-sheet-header__trailing">{trailing}</div>
    {description && <p id={descriptionId}>{description}</p>}
  </header>;
}

export function Sheet({ open, onOpenChange, title, description, children, footer, headerActions, closeLabel = '×', dismissible = true,
  detents = ['medium', 'large'], initialDetent = 'large', className }: SheetProps) {
  const ref = useRef<HTMLDivElement>(null); const id = useId(); const drag = useRef<number | null>(null);
  const [height, setHeight] = useState(fraction(initialDetent)); const [offset, setOffset] = useState(0);
  const dismiss = () => { if (dismissible) onOpenChange(false); };
  const scope = useModal(open, ref, dismiss);
  const step = (direction: number) => {
    const values = [...new Set(detents.map(fraction))].sort((a, b) => a - b);
    const next = direction > 0 ? values.find(value => value > height + .01) : values.toReversed().find(value => value < height - .01);
    if (next !== undefined) setHeight(next);
    else if (direction < 0) dismiss();
  };
  if (!open) return null;
  return createPortal(<ModalParentContext value={scope}><div className="hylo-overlay" onClick={event => { if (event.target === event.currentTarget) dismiss(); }}>
    <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={description ? `${id}-description` : undefined}
      tabIndex={-1} className={clsx('hylo-sheet', className)} data-fixed-detent={detents.length <= 1 || undefined}
      style={{ '--sheet-height': `${height * 100}dvh`, '--sheet-offset': `${offset}px` } as CSSProperties}
      onKeyDown={event => { if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) { event.preventDefault(); step(event.key === 'ArrowUp' ? 1 : -1); } }}>
      <div className="hylo-sheet__handle" aria-hidden="true" onPointerDown={event => {
        drag.current = event.clientY; event.currentTarget.setPointerCapture?.(event.pointerId);
      }} onPointerMove={event => { if (drag.current !== null) setOffset(Math.max(0, event.clientY - drag.current)); }}
      onPointerUp={event => {
        if (drag.current === null) return; const distance = event.clientY - drag.current; drag.current = null; setOffset(0);
        if (distance > 100) dismiss(); else if (Math.abs(distance) > 30) step(distance < 0 ? 1 : -1);
      }} onPointerCancel={() => { drag.current = null; setOffset(0); }}><span /></div>
      <SheetHeader className="hylo-sheet__header" title={title} titleId={`${id}-title`}
        description={description} descriptionId={`${id}-description`}
        leading={dismissible && closeLabel !== '×' ? <Button variant="plain" onClick={dismiss}>{closeLabel}</Button> : undefined}
        trailing={<>{headerActions}{dismissible && closeLabel === '×' && <IconButton name="xmark" label={closeLabel} onClick={dismiss} />}</>} />
      <div className="hylo-sheet__body">{children}</div>{footer && <footer className="hylo-sheet__footer">{footer}</footer>}
    </div></div></ModalParentContext>, document.body);
}

export interface AlertDialogProps { open: boolean; onOpenChange: (open: boolean) => void; title: string; message?: ReactNode;
  confirmLabel: string; cancelLabel: string; onConfirm: () => void | Promise<void>; destructive?: boolean; loading?: boolean; className?: string }
export function AlertDialog({ open, onOpenChange, title, message, confirmLabel, cancelLabel, onConfirm, destructive, loading, className }: AlertDialogProps) {
  const ref = useRef<HTMLDivElement>(null); const id = useId();
  const scope = useModal(open, ref, () => { if (!loading) onOpenChange(false); });
  if (!open) return null;
  return createPortal(<ModalParentContext value={scope}><div className="hylo-overlay hylo-overlay--alert" onClick={event => {
    if (event.target === event.currentTarget && !loading) onOpenChange(false);
  }}><div ref={ref} role="alertdialog" aria-modal="true" tabIndex={-1} aria-labelledby={`${id}-title`}
    aria-describedby={message ? `${id}-message` : undefined} className={clsx('hylo-alert', className)}>
    <h2 id={`${id}-title`}>{title}</h2>{message && <div id={`${id}-message`} className="hylo-alert__message">{message}</div>}
    <div className="hylo-alert__actions"><Button variant="neutral" data-autofocus disabled={loading} onClick={() => onOpenChange(false)}>{cancelLabel}</Button>
      <Button variant={destructive ? 'destructive' : 'prominent'} loading={loading} onClick={() => { void onConfirm(); }}>{confirmLabel}</Button></div>
  </div></div></ModalParentContext>, document.body);
}
